// ============================================================================
// CVR (Cycle Value Rating) Display Utilities
// ============================================================================
// CVR v6 — 0-100 composite scale computed server-side (cvrEngine.js)
// 8 components: Production, Approach, Power/Stuff, Speed/Defense,
//               Situational, Durability, Contract, Trajectory
//
// This module provides display helpers for the pre-computed CVR values.
// All CVR calculation happens on the backend during data ingestion.
// ============================================================================

/**
 * Get CVR display properties (color, description, grade, emoji) for a CVR score.
 * Auto-detects the scale:
 *   - Legacy 0-2 scale (from calculatePlayerCVR in pullBoxscoreRedis.cjs)
 *   - Modern 0-100 scale (from calculateUniversalCVR / cvrEngine v6)
 * Values > 5 are treated as 0-100 scale; values <= 5 as 0-2 scale.
 *
 * @param {number|null} cvr - CVR score (either scale)
 * @returns {{ value: string, color: string, description: string, emoji: string, grade: string }}
 */
export const getCVRDisplay = (cvr) => {
  if (cvr == null || cvr === 0) {
    return {
      value: '—',
      color: '#9e9e9e',
      description: 'No data',
      emoji: '❓',
      grade: '—',
    };
  }

  // Auto-detect scale: > 5 = 0-100 scale, <= 5 = legacy 0-2 scale
  if (cvr > 5) {
    // ── 0-100 scale (universalCVR / cvrEngine v6) ──
    if (cvr >= 85) {
      return { value: Math.round(cvr).toString(), color: '#FFD700', description: 'Elite', emoji: '🏆', grade: 'A+' };
    } else if (cvr >= 70) {
      return { value: Math.round(cvr).toString(), color: '#C0C0C0', description: 'All-Star', emoji: '⭐', grade: 'A' };
    } else if (cvr >= 55) {
      return { value: Math.round(cvr).toString(), color: '#4CAF50', description: 'Above Average', emoji: '📈', grade: 'B+' };
    } else if (cvr >= 40) {
      return { value: Math.round(cvr).toString(), color: '#2196F3', description: 'Average', emoji: '📊', grade: 'B' };
    } else if (cvr >= 25) {
      return { value: Math.round(cvr).toString(), color: '#FF9800', description: 'Below Average', emoji: '📉', grade: 'C' };
    } else if (cvr >= 10) {
      return { value: Math.round(cvr).toString(), color: '#FF5722', description: 'Replacement', emoji: '🔄', grade: 'D' };
    } else {
      return { value: Math.round(cvr).toString(), color: '#F44336', description: 'Minimal', emoji: '⬇️', grade: 'F' };
    }
  }

  // ── Legacy 0-2 scale (calculatePlayerCVR — salary-weighted) ──
  // 1.0 = average value for money, 2.0+ = elite
  const display = cvr.toFixed(2);
  if (cvr >= 1.8) {
    return { value: display, color: '#FFD700', description: 'Elite', emoji: '🏆', grade: 'A+' };
  } else if (cvr >= 1.5) {
    return { value: display, color: '#C0C0C0', description: 'All-Star', emoji: '⭐', grade: 'A' };
  } else if (cvr >= 1.2) {
    return { value: display, color: '#4CAF50', description: 'Above Average', emoji: '📈', grade: 'B+' };
  } else if (cvr >= 1.0) {
    return { value: display, color: '#2196F3', description: 'Average', emoji: '📊', grade: 'B' };
  } else if (cvr >= 0.7) {
    return { value: display, color: '#FF9800', description: 'Below Average', emoji: '📉', grade: 'C' };
  } else if (cvr >= 0.4) {
    return { value: display, color: '#FF5722', description: 'Replacement', emoji: '🔄', grade: 'D' };
  } else {
    return { value: display, color: '#F44336', description: 'Minimal', emoji: '⬇️', grade: 'F' };
  }
};

/**
 * Format a salary number for display.
 *
 * @param {number} salary - Raw salary number (e.g. 35000000)
 * @returns {string} Formatted string (e.g. "$35.0M")
 */
export const formatSalary = (salary) => {
  if (!salary || salary <= 0) return 'No data';

  if (salary >= 1000000) {
    return `$${(salary / 1000000).toFixed(1)}M`;
  } else if (salary >= 1000) {
    return `$${(salary / 1000).toFixed(0)}K`;
  } else {
    return `$${salary.toLocaleString()}`;
  }
};

/**
 * Get CVR tier label and color for quick reference.
 * Auto-detects 0-2 vs 0-100 scale (same heuristic as getCVRDisplay).
 *
 * @param {number} cvr - CVR score (either scale)
 * @returns {{ tier: string, color: string, emoji: string }}
 */
export const getCVRRanking = (cvr) => {
  if (cvr > 5) {
    // 0-100 scale
    if (cvr >= 85) return { tier: 'Elite', color: '#FFD700', emoji: '🏆' };
    if (cvr >= 70) return { tier: 'All-Star', color: '#C0C0C0', emoji: '⭐' };
    if (cvr >= 55) return { tier: 'Above Average', color: '#4CAF50', emoji: '📈' };
    if (cvr >= 40) return { tier: 'Average', color: '#2196F3', emoji: '📊' };
    if (cvr >= 25) return { tier: 'Below Average', color: '#FF9800', emoji: '📉' };
    return { tier: 'Replacement', color: '#F44336', emoji: '🔄' };
  }
  // 0-2 scale
  if (cvr >= 1.8) return { tier: 'Elite', color: '#FFD700', emoji: '🏆' };
  if (cvr >= 1.5) return { tier: 'All-Star', color: '#C0C0C0', emoji: '⭐' };
  if (cvr >= 1.2) return { tier: 'Above Average', color: '#4CAF50', emoji: '📈' };
  if (cvr >= 1.0) return { tier: 'Average', color: '#2196F3', emoji: '📊' };
  if (cvr >= 0.7) return { tier: 'Below Average', color: '#FF9800', emoji: '📉' };
  return { tier: 'Replacement', color: '#F44336', emoji: '🔄' };
};

/**
 * Determine salary type label from dollar amount.
 *
 * @param {number} salary
 * @returns {string}
 */
export const getSalaryType = (salary) => {
  if (!salary || salary <= 0) return 'unknown';
  if (salary <= 750000) return 'minimum';
  if (salary <= 1000000) return 'rookie';
  if (salary <= 3000000) return 'arbitration';
  if (salary <= 10000000) return 'contract';
  if (salary <= 25000000) return 'premium';
  return 'superstar';
};

/**
 * Get display-friendly salary type description.
 *
 * @param {string} salaryType
 * @returns {string}
 */
export const getSalaryTypeDisplay = (salaryType) => {
  const types = {
    minimum: 'Minimum Wage',
    rookie: 'Rookie Contract',
    arbitration: 'Arbitration Eligible',
    contract: 'Standard Contract',
    premium: 'Premium Contract',
    superstar: 'Superstar Contract',
    unknown: 'Unknown Contract',
  };
  return types[salaryType] || 'Unknown Contract';
};
